import { useCallback, useEffect, useMemo, useState } from "react";
import { InputField } from "@/components/input/InputField";
import { Button } from "./ui/button";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from "./ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import type { LineItem } from "@/interfaces/estimate-detail";
import { unitOptions } from "./estimation-details/xactimate/missing-mapping-table/hooks/useMissingMappingTable";
import type { IXactimateUserRowPayload } from "@/interfaces/payloads/xactimate";
import { ACTIVITY_OPTIONS } from "@/constants/xactimate";
import { getDefaultActivity } from "./estimation-details/xactimate/utils/xactimate";
import { XactimateCodeDisplay } from "./XactimateCodeDisplay";
import { XACTIMATE_CATEGORIES } from "@rebuild/shared";

interface MappingDisplayProps {
  cat?: string;
  selCode?: string;
  lineItem?: LineItem;
  onConfirm?: (mapping: IXactimateUserRowPayload) => void;
}

export const MappingDisplay = ({ cat, selCode, lineItem, onConfirm }: MappingDisplayProps) => {
  const [newCat, setNewCat] = useState<string | null>(null);
  const [newSelCode, setNewSelCode] = useState<string | null>(null);

  const defaultQuantity = useMemo(() => (lineItem?.formula?.base.length ? lineItem?.formula?.base : null), [lineItem]);
  const defaultActivity = useMemo(() => getDefaultActivity(lineItem?.lineItem ?? ""), [lineItem]);
  const [quantity, setQuantity] = useState<string | null>(defaultQuantity?.length ? defaultQuantity.toString() : null);
  const [activity, setActivity] = useState<string | null>(defaultActivity.length ? defaultActivity : null);

  const resetForm = useCallback(() => {
    setNewCat(null);
    setNewSelCode(null);
    setActivity(defaultActivity);
    setQuantity(defaultQuantity ? defaultQuantity.toString() : null);
  }, [defaultActivity, defaultQuantity]);

  useEffect(() => {
    resetForm();
  }, [lineItem, resetForm]);

  const catOptions = useMemo(() => {
    return XACTIMATE_CATEGORIES.map((cat) => ({ label: cat, value: cat }));
  }, []);

  const isFormValid = Boolean(newCat && newSelCode);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid && onConfirm) {
      const mapping: IXactimateUserRowPayload = {
        cat: newCat ?? "",
        variable: newSelCode ?? "",
        activity: defaultActivity ?? activity ?? "",
        quantity: defaultQuantity ?? quantity ?? "SF",
        acceptedLineString: lineItem?.lineItem ?? "",
      };
      onConfirm(mapping);
      resetForm();
    }
  };

  // If cat or selCode is not provided, render the editable form
  if ((!cat || !selCode) && onConfirm) {
    // Ensure callback handlers are provided for editable mode
    return (
      <div className="my-2 flex w-fit flex-col items-start gap-4">
        <p className="ite text-sm font-medium text-gray-500">No mapping found, please add one.</p>
        <form onSubmit={handleSubmit} className="w-full">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-1">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">CAT</p>
                <Combobox
                  items={catOptions}
                  itemToStringValue={(option) => option.label}
                  value={catOptions.find((opt) => opt.value === newCat) ?? null}
                  onValueChange={(item) => setNewCat(item?.value ?? null)}
                >
                  <ComboboxInput showClear={!!newCat} />
                  <ComboboxContent className="pointer-events-auto">
                    <ComboboxEmpty>No results found</ComboboxEmpty>
                    <ComboboxList className="max-h-60 overflow-y-auto">
                      {(option) => (
                        <ComboboxItem key={option.value} value={option}>
                          {option.label}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">SEL</p>
                <InputField
                  name="sel-code"
                  value={newSelCode ?? ""} // Use current selCode or empty string
                  onChange={(event) => setNewSelCode(event.target.value)}
                  placeHolder="Enter SEL"
                />
              </div>
            </div>
            <div className="flex flex-row gap-1">
              {
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">Activity</p>
                  <Select value={activity ?? ""} onValueChange={(value) => setActivity(value)} defaultValue={defaultActivity}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Activity" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVITY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              }
              {!defaultQuantity && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">Quantity</p>
                  <Select onValueChange={(value) => setQuantity(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="select" />
                    </SelectTrigger>
                    <SelectContent>
                      {unitOptions
                        .filter((option) => option.value.length < 4)
                        .map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <Button type="submit" variant="ghost" className="mt-4 self-end" disabled={!isFormValid}>
            Confirm
          </Button>
        </form>
      </div>
    );
  } else if (!cat || !selCode) {
    return (
      <div className="my-2 flex flex-row items-center gap-2">
        <p className="text-sm font-medium">No mapping found.</p>
      </div>
    );
  } else {
    // Both cat and selCode are provided, render display mode
    return <XactimateCodeDisplay cat={cat} selCode={selCode} />;
  }
};
